// @noflow
import { ACCOUNT_ROUTES } from '@/routes'
import { format, parseISO } from 'date-fns'
import Handlebars from 'handlebars'
import i18next from 'i18next'
import Cookies from 'js-cookie'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { localeToDateLocale } from '@/utils/countryCodeHelper'

import useInAccountRaf from '@/hooks/useInAccountRaf/useInAccountRaf'

import { Language } from '@/types'

import CopyInputButton from '../CopyInputButton/CopyInputButton'
import CountdownModal from '../CountdownModal/CountdownModal'

type Props = {
  isModalOpen: boolean
  setOpenModal: (isOpen: boolean) => void
}

const RAF_CAMPAIGN_COOKIE_KEY = 'has_closed_campaign_modal'

const RafCampaignModal = ({
  isModalOpen,
  setOpenModal
}: Props): JSX.Element | null => {
  const navigate = useNavigate()

  const namespace = 'dashboard'

  const {
    customerDiscountCopy,
    referralDiscountCopy,
    copyLinkMessage,
    campaignEndDate,
    discountCode,
    data,
    shippingCountryCode,
    campaignName
  } = useInAccountRaf()

  const showModal = Cookies.get(RAF_CAMPAIGN_COOKIE_KEY) !== campaignName

  const { user } = data || {}
  const { subscription } = user || {}
  const { activeRafCampaignVariant } = subscription || {}
  const rafCampaignTemplateData = activeRafCampaignVariant?.view?.data

  const dateLocale = localeToDateLocale(
    shippingCountryCode,
    i18next.language as Language
  )

  const compiledHeading = Handlebars.compile(
    rafCampaignTemplateData?.modal?.heading || ''
  )({
    referrerDiscountFirstBox: customerDiscountCopy?.[0] ?? 'X%',
    referralDiscountFirstBox: referralDiscountCopy?.[0] ?? 'X%',
    referralDiscountSecondBox: referralDiscountCopy?.[1] ?? 'X%'
  })

  const compiledDescription = Handlebars.compile(
    rafCampaignTemplateData?.modal?.description || ''
  )({
    referrerDiscountFirstBox: customerDiscountCopy?.[0] ?? 'X%',
    referralDiscountFirstBox: referralDiscountCopy?.[0] ?? 'X%',
    referralDiscountSecondBox: referralDiscountCopy?.[1] ?? 'X%',
    expiryDate: campaignEndDate
      ? format(parseISO(campaignEndDate), 'dd/MM/yyyy', { locale: dateLocale })
      : ''
  })

  const compiledTermsText = Handlebars.compile(
    rafCampaignTemplateData?.modal?.terms
  )({
    expiryDate: campaignEndDate
      ? format(parseISO(campaignEndDate), 'dd/MM/yyyy', { locale: dateLocale })
      : ''
  })

  const setRafCampaignCookie = useCallback(() => {
    campaignName &&
      Cookies.set(RAF_CAMPAIGN_COOKIE_KEY, campaignName, {
        path: '/account'
      })
  }, [campaignName])

  const navigateToCampaignPage = useCallback(() => {
    setRafCampaignCookie()

    navigate(ACCOUNT_ROUTES.referAFriend)
  }, [navigate, setRafCampaignCookie])

  const toggleModal = useCallback(
    (open: boolean) => {
      if (!open) {
        setRafCampaignCookie()
      }
      setOpenModal(open)
    },
    [setOpenModal, setRafCampaignCookie]
  )

  return (
    <CountdownModal
      isModalOpen={isModalOpen && showModal}
      toggleModal={toggleModal}
      heroImageSlug={rafCampaignTemplateData?.modal?.image_slug}
      heroImageAlt={rafCampaignTemplateData?.modal?.image_alt}
      countdownEndDate={campaignEndDate}
      headingText={{
        text: compiledHeading,
        variant: 'display24',
        align: 'center',
        margin: false,
        translate: false
      }}
      descriptionText={{
        text: compiledDescription,
        variant: 'textRegular12',
        align: 'center',
        margin: false,
        translate: false
      }}
      buttonText={{
        text: rafCampaignTemplateData?.modal?.cta,
        translate: false
      }}
      footerText={{
        text: compiledTermsText,
        translate: false
      }}
      footerLink={rafCampaignTemplateData?.modal?.terms_url || ''}
      buttonOnClick={navigateToCampaignPage}
    >
      {discountCode && (
        <CopyInputButton
          copyLinkMessage={copyLinkMessage}
          namespace={namespace}
          referralCode={discountCode}
        />
      )}
    </CountdownModal>
  )
}

export default RafCampaignModal
